<script setup>
import { ref, onMounted, watch, computed, defineProps, defineEmits } from 'vue';
import { TrashIcon } from '@heroicons/vue/24/solid';
import Data from '@/api/data';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  },
  dataID: {
    type: String,
    required: true
  },
  widgetTitle: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue']);

const subcollections = ref({});
const source = ref(props.modelValue.comparison?.split(' ')[0] || '');
const operation = ref(props.modelValue.comparison?.split(' ')[1] || '');
const constant = ref(props.modelValue.comparison?.split(' ')[2] || '');
const startFrom = ref(props.modelValue.startDate || '');
const variables = ref(props.modelValue.values || [{source: '', field: ''}]);
const loading = ref(false);
const sourceInputRef = ref(null);

onMounted(async() => {
  await getSubcollections();
  updateConfig();
});

const getSubcollections = async() => {
  loading.value = true;
  const response = await Data.get(props.dataID);
  if (response.body.status === 'success') {
    const ignoreSubcollections = ['Net_Cfg', 'action_request', 'action_result', 'location', 'messages'];
      subcollections.value = Object.fromEntries(
        Object.entries(response.body.details)
          .filter(([key]) => !ignoreSubcollections.includes(key))
          .sort(([a], [b]) => a.localeCompare(b))
      );
  }
  loading.value = false;
};

const onSourceChange = (index, event) => {
  variables.value[index].source = event;
};

const insertVariable = (variableSource, variableField) => {
  const input = sourceInputRef.value;
  if (!input) return;

  const variableText = `${variableSource}.${variableField}`;
  const cursorPos = input.selectionStart;
  const textBefore = source.value.substring(0, cursorPos);
  const textAfter = source.value.substring(cursorPos);

  source.value = textBefore + variableText + textAfter;

  // Set cursor position after the inserted text
  setTimeout(() => {
    const newCursorPos = cursorPos + variableText.length;
    input.setSelectionRange(newCursorPos, newCursorPos);
    input.focus();
  }, 0);
};

const updateConfig = () => {
  const formattedName = props.widgetTitle.replace(/\s+/g, '_');
  const comparisonOperation = source.value && operation.value && constant.value 
    ? `${source.value} ${operation.value} ${constant.value}`
    : '';

  const config = {
    columns: ["startUTC", "endUTC", "duration", "tag"],
    source: formattedName,
    title: props.widgetTitle,
    comparison: comparisonOperation,
    startDate: startFrom.value,
    values: variables.value,
  };

  emit('update:modelValue', config);
};

const isValid = computed(() => {
  return props.widgetTitle.trim() !== '' && 
         source.value !== '' && 
         operation.value !== '' && 
         constant.value !== '' && 
         startFrom.value !== '' &&
         variables.value.length > 0 &&
         variables.value.every(v => v.source && v.field);
});

watch([() => props.widgetTitle, source, operation, constant, startFrom, variables], () => {
  updateConfig();
}, { deep: true });
</script>

<template>
  <div class="space-y-4">
    <!-- Variables -->
    <div>
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        Variables *
        <span v-if="loading" class="inline-flex items-center ml-2" title="Loading options...">
          <span class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
        </span>
      </label>
      <div v-for="(value, index) in variables" :key="index" class="flex gap-2 mb-2">
        <div v-if="variables.length > 1" 
             class="flex items-center hover:cursor-pointer" 
             @click="variables.splice(index, 1)">
          <TrashIcon class="h-5 w-5 text-red-600"/>
        </div>

        <div class="flex-1">
          <select 
            :value="value.source" 
            @change="onSourceChange(index, $event.target.value)" 
            :disabled="loading"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="">Select source...</option>
            <option v-for="subcollection in Object.keys(subcollections)" 
                    :key="subcollection" 
                    :value="subcollection">
              {{ subcollection }}
            </option>
          </select>
        </div>

        <div class="flex-1">
          <select 
            :value="value.field || ''" 
            @change="variables[index].field = $event.target.value" 
            :disabled="!value.source || loading"
            class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
          >
            <option value="">Select field...</option>
            <option v-for="field in Object.keys(subcollections[value.source] ?? []).filter(key => 
                      subcollections[value.source][key] === 'int' || 
                      subcollections[value.source][key] === 'float').sort()" 
                    :key="field" 
                    :value="field">
              {{ field }}
            </option>
          </select>
        </div>

        <button 
          v-if="value.source && value.field" 
          type="button"
          @click="insertVariable(value.source, value.field)" 
          class="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm whitespace-nowrap"
        >
          Insert
        </button>
      </div>

      <button 
        type="button" 
        @click="variables.push({source: '', field: ''})" 
        class="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
      >
        + Add another variable
      </button>
    </div>

      <!-- Comparison Operation -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Equation/Comparison *
          <span class="font-normal text-xs text-gray-500 dark:text-gray-400 ml-2">
            (e.g. source.field + source2.field2 or source.field * 2)
          </span>
        </label>
        <div class="flex gap-2">
          <div class="flex-1">
            <input
              ref="sourceInputRef"
              v-model="source"
              type="text"
              placeholder="e.g. source.field + source2.field2"
              class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div class="flex-none">
            <select 
              v-model="operation"
              class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Op</option>
              <option value=">">&gt;</option>
              <option value=">=">&gt;=</option>
              <option value="<">&lt;</option>
              <option value="<=">&lt;=</option>
              <option value="==">=</option>
              <option value="!=">!=</option>
            </select>
          </div>

          <div class="flex-none">
            <input
              v-model="constant"
              type="text"
              placeholder="Value"
              class="w-24 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      <!-- Start From -->
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Start From *
        </label>
        <input
          v-model="startFrom"
          type="date"
          class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      <!-- Validation Message -->
      <div v-if="!isValid" class="text-sm text-orange-600 dark:text-orange-400">
        * All fields are required
      </div>
  </div>
</template>
